<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div class="main-container">
        <div class="articulo-principal property-detail-view">
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Cargando información...</p>
          </div>

          <div v-else-if="!propiedad" class="error-state">
            <p>No se encontró la propiedad.</p>
            <button class="btn-secondary" @click="goBack">Volver</button>
          </div>

          <div v-else class="content-wrapper">
            <header class="page-header">
              <button class="back-btn" type="button" @click="goBack">
                <b-icon icon="arrow-left" aria-hidden="true" />
                <span class="sr-only">Volver</span>
              </button>
              <div class="header-content">
                <div class="breadcrumbs">
                  <span class="crumb-link" @click="goBack">Propiedades</span>
                  <span class="separator">/</span>
                  <span class="current">{{ propiedad.nombre || propiedad.direccion }}</span>
                </div>
                <h1>Detalles de la propiedad</h1>
                <p class="lede">Actualiza la información y adjunta imágenes relevantes.</p>
              </div>
              <div class="actions-bar">
                <button class="btn-secondary" @click="openEdit">
                  <b-icon icon="pencil" aria-hidden="true" />
                  Editar propiedad
                </button>
                <button class="btn-primary" @click="openNoteModal">
                  <b-icon icon="journal-plus" aria-hidden="true" />
                  Agregar nota
                </button>
              </div>
            </header>

            <section class="section-card info-card">
              <h2 class="section-title">INFORMACIÓN GENERAL</h2>
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">DIRECCIÓN</span>
                  <div class="info-value-group">
                    <b-icon icon="geo-alt" class="icon" aria-hidden="true" />
                    <div>
                      <div class="main-text">{{ propiedad.direccion }}</div>
                      <div class="sub-text">
                        {{ propiedad.numero_apartamento ? 'Depto ' + propiedad.numero_apartamento : '' }}
                      </div>
                      <div class="sub-text">{{ getComunaName(propiedad) }}</div>
                    </div>
                  </div>
                </div>

                <div class="info-item">
                  <span class="info-label">TIPO DE PROPIEDAD</span>
                  <div class="info-value-group">
                    <b-icon icon="building" class="icon" aria-hidden="true" />
                    <span class="main-text">{{ propiedad.tipo_propiedad || 'Propiedad' }}</span>
                  </div>
                </div>

                <div class="info-item">
                  <span class="info-label">PERSONA DE CONTACTO</span>
                  <div class="info-value-group">
                    <b-icon icon="person" class="icon" aria-hidden="true" />
                    <div>
                      <div class="main-text">{{ propiedad.contacto_nombre || 'No asignado' }}</div>
                      <div class="sub-text" v-if="propiedad.contacto_telefono">{{ propiedad.contacto_telefono }}</div>
                      <div class="sub-text" v-if="propiedad.contacto_email">{{ propiedad.contacto_email }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="section-card images-card">
              <div class="card-header-clean">
                <h2 class="section-title-clean">IMÁGENES DE LA PROPIEDAD</h2>
                <span class="count-badge-clean">{{ propertyImages.length }} fotos</span>
              </div>
              <input
                ref="propertyPhotoInput"
                class="sr-only"
                type="file"
                accept="image/*"
                multiple
                @change="handlePhotoUpload"
              />
              <div class="images-grid">
                <div v-if="propertyImages.length === 0" class="empty-images">
                  <p>No hay fotos todavía.</p>
                </div>
                <div v-for="(img, idx) in propertyImages" :key="img.id || idx" class="image-wrapper" :class="{ 'is-main': img.isMain }">
                  <button
                    class="property-image-btn"
                    type="button"
                    :aria-label="`Ver foto ${idx + 1}`"
                    @click="openImage(img)"
                  >
                    <img :src="img.link" alt="Foto propiedad" />
                  </button>
                  <div class="image-actions">
                    <button
                      class="main-photo-btn"
                      type="button"
                      :disabled="updatingMainPhoto || img.isMain || !img.id || deletingImageId === img.id"
                      :aria-label="img.isMain ? 'Foto principal' : 'Usar como principal'"
                      @click.stop="setMainPhoto(img)"
                    >
                      <b-icon icon="star-fill" aria-hidden="true" />
                    </button>
                    <button
                      class="delete-photo-btn"
                      type="button"
                      :disabled="deletingImageId === img.id || !img.id"
                      :aria-label="`Eliminar foto ${idx + 1}`"
                      @click.stop="deletePhoto(img)"
                    >
                      <b-icon icon="trash" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <button class="add-photo-btn" type="button" @click="triggerPhotoUpload" :disabled="uploadingImages">
                  <div class="circle-icon">
                    <b-icon icon="camera" aria-hidden="true" />
                  </div>
                  <span>{{ uploadingImages ? 'Subiendo...' : 'Agregar foto' }}</span>
                </button>
              </div>
            </section>

            <section class="section-card">
              <div class="card-header-clean">
                <h2 class="section-title-clean">SOLICITUDES ACTIVAS</h2>
                <span class="status-badge blue">{{ solicitudesActivas.length }} EN CURSO</span>
              </div>

              <div v-if="solicitudesActivas.length === 0" class="empty-list">
                <p>No hay solicitudes activas en este momento.</p>
              </div>

              <div v-else class="requests-list">
                <button
                  v-for="req in solicitudesActivas"
                  :key="req.id"
                  class="request-item"
                  type="button"
                  @click="goToRequest(req)"
                >
                  <div class="request-icon">
                    <div class="service-icon" :style="getCategoryIconStyle(req)">
                      <span v-if="categoryIcon(req)" class="service-icon__svg" v-html="categoryIcon(req)" aria-hidden="true"></span>
                      <b-icon v-else icon="tools" aria-hidden="true" />
                    </div>
                  </div>
                  <div class="request-info">
                    <h3 class="request-title">{{ req.titulo }}</h3>
                    <p class="request-meta">Solicitado {{ formatDate(req.fecha_creado) }}</p>
                    <div v-if="getRespondingProfessionals(req).length" class="req-avatars">
                      <div
                        v-for="pro in visibleProfessionals(req)"
                        :key="pro.id"
                        class="req-avatar"
                        :style="getAvatarStyle(pro)"
                      >
                        <span v-if="!pro.foto_url" class="req-avatar-initials">
                          {{ getInitials(pro.display_name || pro.nombre || '') }}
                        </span>
                      </div>
                      <div v-if="extraProfessionalsCount(req)" class="req-avatar req-avatar--more">
                        +{{ extraProfessionalsCount(req) }}
                      </div>
                    </div>
                    <p v-if="orderProfessionalLabel(req)" class="request-assigned">
                      Atendido por <span>{{ orderProfessionalLabel(req) }}</span>
                    </p>
                  </div>
                  <span class="status-pill" :class="getStatusClass(req)">{{ req.estado_label || 'En curso' }}</span>
                </button>
              </div>
            </section>

            <section class="section-card">
              <div class="history-section-header">
                <h2 class="section-title">HISTORIAL</h2>
                <button class="btn-secondary btn-secondary--compact" type="button" @click="openNoteModal">
                  <b-icon icon="journal-plus" aria-hidden="true" />
                  Agregar nota
                </button>
              </div>

              <div v-if="timelineItems.length === 0" class="empty-list">
                <p>No hay historial de servicios ni notas registradas.</p>
              </div>

              <div v-else class="history-list">
                <article v-for="item in timelineItems" :key="item.key" class="history-card">
                  <div class="history-header">
                    <span class="history-date">{{ item.displayDate }}</span>
                    <span
                      class="history-badge"
                      :class="item.type === 'servicio' ? 'history-badge--service' : 'history-badge--note'"
                    >
                      {{ item.type === 'servicio' ? 'Servicio' : 'Nota' }}
                    </span>
                  </div>
                  <div v-if="item.type === 'servicio'">
                    <h3 class="history-title">{{ item.servicio || 'Servicio' }}</h3>
                    <p class="history-meta">Profesional: {{ item.profesional || 'Profesional' }}</p>
                    <p class="history-meta">Monto: {{ formatCurrency(item.monto) }}</p>
                    <button v-if="item.token" class="text-btn" type="button" @click="viewOrder(item)">
                      Ver orden
                    </button>
                  </div>
                  <div v-else>
                    <p class="note-text">{{ item.texto }}</p>
                    <div v-if="item.imagenes && item.imagenes.length" class="note-images">
                      <button
                        v-for="(img, idx) in item.imagenes"
                        :key="img.id || idx"
                        type="button"
                        class="note-image-btn"
                        :aria-label="`Ver imagen de nota ${idx + 1}`"
                        @click="openImage(img)"
                      >
                        <img :src="img.link" alt="Imagen de nota" />
                      </button>
                    </div>
                  </div>
                </article>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showImageModal && selectedImage"
      class="image-modal-backdrop"
      role="dialog"
      aria-modal="true"
      :aria-label="selectedImage.isMain ? 'Foto principal ampliada' : 'Foto ampliada'"
      @click.self="closeImage"
    >
      <div class="image-modal-card">
        <button class="image-modal-close" type="button" @click="closeImage" aria-label="Cerrar imagen">
          <b-icon icon="x" aria-hidden="true" />
        </button>
        <img
          class="image-modal-photo"
          :src="selectedImage.link"
          :alt="selectedImage.isMain ? 'Foto principal ampliada' : 'Foto ampliada'"
        />
        <div v-if="selectedImage.fecha_creado || selectedImage.fecha || selectedImage.isMain" class="image-modal-footer">
          <template v-if="selectedImage.fecha_creado || selectedImage.fecha">
            <b-icon icon="calendar-event" aria-hidden="true" />
            <span>{{ formatDate(selectedImage.fecha_creado || selectedImage.fecha) }}</span>
          </template>
          <template v-if="selectedImage.isMain">
            <b-icon icon="star-fill" aria-hidden="true" />
            Foto principal
          </template>
        </div>
      </div>
    </div>

    <div v-if="showEditModal" class="modal-backdrop" @click.self="closeEdit">
      <div class="modal-container">
        <div class="modal-header">
          <h2 class="form-title">Editar propiedad</h2>
          <button class="close-modal-btn" @click="closeEdit">
            <b-icon icon="x" aria-hidden="true" />
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <label class="field">
              <span class="field__label">Nombre o referencia</span>
              <input v-model="editForm.nombre" type="text" placeholder="Ej: Torre Norte" />
            </label>

            <label class="field">
              <span class="field__label">Dirección</span>
              <input v-model="editForm.direccion" type="text" placeholder="Ingresa la dirección completa" />
            </label>

            <label class="field">
              <span class="field__label">Comuna</span>
              <select v-model="editForm.comuna_id">
                <option :value="null">Selecciona una comuna</option>
                <option v-for="c in comunas" :key="c.id" :value="c.id">
                  {{ c.Nombre }}
                </option>
              </select>
            </label>

            <div class="field-row">
              <label class="field">
                <span class="field__label">Tipo de propiedad</span>
                <select v-model="editForm.tipo_propiedad">
                  <option value="">Seleccionar</option>
                  <option value="Departamento">Departamento</option>
                  <option value="Casa">Casa</option>
                  <option value="Oficina">Oficina</option>
                  <option value="Local">Local</option>
                  <option value="Bodega">Bodega</option>
                </select>
              </label>
              <label class="field">
                <span class="field__label">Número/departamento</span>
                <input v-model="editForm.numero_apartamento" type="text" placeholder="Ej: 1204" />
              </label>
            </div>

            <div class="form-divider">
              <span>Contacto del arrendatario (opcional)</span>
            </div>

            <div class="field-row">
              <label class="field">
                <span class="field__label">Nombre (opcional)</span>
                <input v-model="editForm.contacto_nombre" type="text" placeholder="Nombre del contacto" />
              </label>
              <label class="field">
                <span class="field__label">Teléfono (opcional)</span>
                <input v-model="editForm.contacto_telefono" type="text" placeholder="Teléfono del contacto" />
              </label>
            </div>

            <label class="field">
              <span class="field__label">Email (opcional)</span>
              <input v-model="editForm.contacto_email" type="email" placeholder="correo@ejemplo.com" />
            </label>

            <p v-if="editError" class="field__error">
              <b-icon icon="exclamation-circle" aria-hidden="true" />
              {{ editError }}
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <div class="modal-footer-right">
            <button class="btn-secondary" type="button" @click="closeEdit">
              Cancelar
            </button>
            <button class="btn-primary" type="button" :disabled="savingEdit" @click="saveEdit">
              {{ savingEdit ? 'Guardando...' : 'Guardar cambios' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showNoteModal" class="modal-backdrop" @click.self="closeNoteModal">
      <div class="modal-container modal-container--compact">
        <div class="modal-header">
          <h2 class="form-title">Agregar nota</h2>
          <button class="close-modal-btn" @click="closeNoteModal">
            <b-icon icon="x" aria-hidden="true" />
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <label class="field">
              <span class="field__label">Nota</span>
              <textarea v-model="noteText" rows="4" placeholder="Escribe una nota sobre esta propiedad..."></textarea>
            </label>

            <label class="field">
              <span class="field__label">Imágenes (opcional)</span>
              <input type="file" accept="image/*" multiple @change="handleNoteFiles" />
            </label>

            <div v-if="noteFiles.length" class="file-list">
              <span v-for="(file, index) in noteFiles" :key="file.name + index" class="file-chip">
                {{ file.name }}
                <button type="button" @click="removeNoteFile(index)">×</button>
              </span>
            </div>

            <p v-if="noteFileError" class="field__error">
              <b-icon icon="exclamation-circle" aria-hidden="true" />
              {{ noteFileError }}
            </p>

            <p v-if="noteError" class="field__error">
              <b-icon icon="exclamation-circle" aria-hidden="true" />
              {{ noteError }}
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <div class="modal-footer-right">
            <button class="btn-secondary" type="button" @click="closeNoteModal">
              Cancelar
            </button>
            <button class="btn-primary" type="button" :disabled="noteSaving" @click="submitNote">
              {{ noteSaving ? 'Guardando...' : 'Guardar nota' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import TopNav from '@/components/TopNav.vue'
import SideNav from '@/components/SideNav.vue'

export default {
  name: 'PropiedadDetalleAdministrador',
  components: {
    TopNav,
    SideNav
  },
  data() {
    return {
      loading: true,
      propiedad: null,
      propertyImages: [],
      propertyNotes: [],
      solicitudesActivas: [],
      historialServicios: [],
      showImageModal: false,
      selectedImage: null,
      propertyMainId: null,
      uploadingImages: false,
      updatingMainPhoto: false,
      deletingImageId: null,
      showEditModal: false,
      savingEdit: false,
      editError: '',
      editForm: {
        nombre: '',
        direccion: '',
        comuna_id: null,
        tipo_propiedad: '',
        numero_apartamento: '',
        contacto_nombre: '',
        contacto_email: '',
        contacto_telefono: ''
      },
      showNoteModal: false,
      noteText: '',
      noteFiles: [],
      noteSaving: false,
      noteError: '',
      noteFileError: ''
    }
  },
  computed: {
    ...mapGetters(['comunas', 'categorias']),
    timelineItems() {
      const serviceTimeline = this.historialServicios.map((item) => ({
        ...item,
        key: `servicio-${item.id}`,
        type: 'servicio',
        date: item.fecha_iso || item.fecha,
        displayDate: item.fecha || this.formatDate(item.fecha_iso)
      }))

      const noteTimeline = this.propertyNotes.map((note) => ({
        ...note,
        key: `nota-${note.id}`,
        type: 'nota',
        date: note.fecha,
        displayDate: this.formatDate(note.fecha)
      }))

      return [...serviceTimeline, ...noteTimeline].sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0)
        const dateB = b.date ? new Date(b.date) : new Date(0)
        return dateB - dateA
      })
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.loading = true
      const propId = this.$route.params.id
      axios.get(`${window.hostname}api/property_manager/propiedades/${propId}`)
        .then((res) => {
          this.propiedad = res.data?.propiedad || null
          this.propertyNotes = res.data?.notas_propiedad || []
          this.solicitudesActivas = res.data?.solicitudes_activas || []
          this.historialServicios = res.data?.historial_servicios || []
          this.propertyMainId = this.propiedad?.foto_principal_id || null
          const imagenes = Array.isArray(this.propiedad?.imagenes) ? this.propiedad.imagenes : []
          this.propertyImages = imagenes
            .map((foto) => ({
              id: foto.id,
              link: foto.link,
              fecha_creado: foto.fecha_creado || foto.fecha,
              fecha: foto.fecha,
              isMain: foto.id === this.propertyMainId
            }))
            .filter((foto) => foto.link)
        })
        .catch(() => {
          this.propiedad = null
        })
        .finally(() => {
          this.loading = false
        })
    },
    goBack() {
      this.$router.push('/administrador-propiedades/Propiedades')
    },
    goToRequest() {
      this.$router.push('/administrador-propiedades/Solicitudes')
    },
    getComunaName(prop) {
      if (!prop) {
        return ''
      }
      if (prop.comuna_nombre) {
        return prop.comuna_nombre
      }
      if (!prop.comuna_id || !Array.isArray(this.comunas)) {
        return ''
      }
      const comuna = this.comunas.find(item => item.id === prop.comuna_id)
      return comuna?.Nombre || ''
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) {
        return value
      }
      return numberValue.toLocaleString('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0
      })
    },
    getStatusClass(req) {
      if (!req) {
        return ''
      }
      const estado = (req.estado_label || '').toLowerCase()
      if (estado.includes('visita agendada')) {
        return 'status-pill--scheduled'
      }
      if (estado.includes('confirmado')) {
        return 'status-pill--confirmed'
      }
      if (estado.includes('en proceso')) {
        return 'status-pill--progress'
      }
      if (estado.includes('publicado')) {
        return 'status-pill--active'
      }
      if (estado.includes('pendiente')) {
        return 'status-pill--pending'
      }
      return 'status-pill--neutral'
    },
    categoryData(req) {
      if (!req || !Array.isArray(this.categorias)) {
        return null
      }
      return this.categorias.find((categoria) => categoria.id === req.categoria_id) || null
    },
    categoryColor(req) {
      const color = this.categoryData(req)?.atributos?.color
      return typeof color === 'string' && color.trim() ? color : '#2563eb'
    },
    decodeSvg(svg) {
      if (typeof svg !== 'string') return ''
      const el = document.createElement('textarea')
      el.innerHTML = svg
      const decoded = (el.value || el.textContent || '').trim()
      return decoded || svg
    },
    fixSvg(svg) {
      const raw = this.decodeSvg(svg) || svg || ''
      if (!raw) return ''

      let normalized = raw
        .replace(/fill="[^"]*"/gi, 'fill="currentColor"')
        .replace(/stroke="[^"]*"/gi, 'stroke="currentColor"')

      if (!/style="[^"]*width:[^;]*;?[^\"]*height:[^;]*;?/i.test(normalized)) {
        normalized = normalized.replace(
          /<svg([^>]*)>/i,
          '<svg$1 style="width:26px;height:26px;display:block;">'
        )
      }

      if (!/width="\\d+/i.test(normalized) || !/height="\\d+/i.test(normalized)) {
        normalized = normalized.replace(
          /<svg([^>]*)>/i,
          '<svg$1 width="26" height="26">'
        )
      }

      return normalized
    },
    categoryIcon(req) {
      const icon = this.categoryData(req)?.atributos?.svg || ''
      return this.fixSvg(icon)
    },
    hexToRgba(hex, alpha) {
      if (typeof hex !== 'string') return null
      const sanitized = hex.replace('#', '')
      if (![3, 6].includes(sanitized.length)) return null
      const normalized = sanitized.length === 3
        ? sanitized.split('').map(c => c + c).join('')
        : sanitized
      const num = parseInt(normalized, 16)
      if (Number.isNaN(num)) return null
      const r = (num >> 16) & 255
      const g = (num >> 8) & 255
      const b = num & 255
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    getCategoryIconStyle(req) {
      const color = this.categoryColor(req)
      return {
        background: this.hexToRgba(color, 0.12) || '#e0f2fe',
        color
      }
    },
    getRespondingProfessionals(req) {
      if (!req || !Array.isArray(req.profesionales_respondieron)) {
        return []
      }
      return req.profesionales_respondieron
    },
    orderProfessionalLabel(req) {
      if (!req || !req.orden || !req.orden.profesional) {
        return ''
      }
      const profesional = req.orden.profesional
      const label = profesional.display_name || [profesional.nombre, profesional.apellido].filter(Boolean).join(' ').trim()
      return label || ''
    },
    viewOrder(item) {
      if (!item || !item.token) {
        return
      }
      this.$router.push(`/administrador-propiedades/Orden/${item.token}`)
    },
    visibleProfessionals(req) {
      return this.getRespondingProfessionals(req).slice(0, 3)
    },
    extraProfessionalsCount(req) {
      const total = this.getRespondingProfessionals(req).length
      return total > 3 ? total - 3 : 0
    },
    getInitials(name) {
      if (!name || typeof name !== 'string') {
        return '?'
      }
      return name
        .split(/\\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
    getAvatarStyle(profesional) {
      if (!profesional || !profesional.foto_url) {
        return {}
      }
      return {
        backgroundImage: `url(${profesional.foto_url})`
      }
    },
    openImage(img) {
      if (!img?.link) return
      this.selectedImage = img
      this.showImageModal = true
    },
    closeImage() {
      this.showImageModal = false
      this.selectedImage = null
    },
    triggerPhotoUpload() {
      if (this.uploadingImages) return
      this.$refs.propertyPhotoInput?.click()
    },
    handlePhotoUpload(event) {
      if (this.uploadingImages) {
        return
      }
      const files = Array.from(event.target.files || [])
      if (!files.length) {
        return
      }
      this.uploadingImages = true
      const propId = this.$route.params.id
      const formData = new FormData()
      files.forEach((file) => {
        formData.append('fotos', file)
      })

      axios.post(`${window.hostname}api/property_manager/propiedades/${propId}/fotos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
        .then((res) => {
          const newPhotos = (res.data?.fotos || []).map((foto) => ({
            id: foto.id,
            link: foto.link,
            fecha_creado: foto.fecha_creado || foto.fecha,
            fecha: foto.fecha,
            isMain: foto.id === this.propertyMainId
          }))
          this.propertyImages = [...newPhotos, ...this.propertyImages]
        })
        .catch(() => {
          // ignore
        })
        .finally(() => {
          this.uploadingImages = false
          event.target.value = ''
        })
    },
    setMainPhoto(img) {
      if (!img?.id || this.updatingMainPhoto) return
      this.updatingMainPhoto = true
      const propId = this.$route.params.id
      axios.put(`${window.hostname}api/property_manager/propiedades/${propId}/foto-principal`, {
        foto_id: img.id
      })
        .then(() => {
          this.propertyMainId = img.id
          this.propertyImages = this.propertyImages.map((photo) => ({
            ...photo,
            isMain: photo.id === img.id
          }))
        })
        .finally(() => {
          this.updatingMainPhoto = false
        })
    },
    deletePhoto(img) {
      if (!img?.id || this.deletingImageId) return
      if (!confirm('¿Seguro que quieres eliminar esta foto?')) return
      this.deletingImageId = img.id
      const propId = this.$route.params.id
      axios.delete(`${window.hostname}api/property_manager/propiedades/${propId}/fotos/${img.id}`)
        .then(() => {
          this.propertyImages = this.propertyImages.filter(photo => photo.id !== img.id)
        })
        .finally(() => {
          this.deletingImageId = null
        })
    },
    openEdit() {
      this.editForm = {
        nombre: this.propiedad.nombre || '',
        direccion: this.propiedad.direccion || '',
        comuna_id: this.propiedad.comuna_id || null,
        tipo_propiedad: this.propiedad.tipo_propiedad || '',
        numero_apartamento: this.propiedad.numero_apartamento || '',
        contacto_nombre: this.propiedad.contacto_nombre || '',
        contacto_email: this.propiedad.contacto_email || '',
        contacto_telefono: this.propiedad.contacto_telefono || ''
      }
      this.editError = ''
      this.showEditModal = true
    },
    closeEdit() {
      if (this.savingEdit) return
      this.showEditModal = false
    },
    saveEdit() {
      if (!this.editForm.direccion.trim()) {
        this.editError = 'La dirección es obligatoria.'
        return
      }
      this.savingEdit = true
      const propId = this.$route.params.id
      axios.put(`${window.hostname}api/property_manager/propiedades/${propId}`, this.editForm)
        .then(() => {
          this.showEditModal = false
          this.fetchData()
        })
        .catch((err) => {
          this.editError = err.response?.data?.message || 'Error al guardar.'
        })
        .finally(() => {
          this.savingEdit = false
        })
    },
    openNoteModal() {
      this.noteText = ''
      this.noteFiles = []
      this.noteError = ''
      this.noteFileError = ''
      this.showNoteModal = true
    },
    closeNoteModal() {
      if (this.noteSaving) return
      this.showNoteModal = false
    },
    handleNoteFiles(event) {
      const files = Array.from(event.target.files || [])
      if (!files.length) return
      const allowedExtensions = ['jpg', 'png', 'jpeg', 'webp', 'gif']
      let invalidFound = false

      files.forEach((file) => {
        const extension = (file && file.name && file.name.split('.').pop())
          ? file.name.split('.').pop().toLowerCase()
          : ''
        if (file && allowedExtensions.includes(extension)) {
          const alreadyAdded = this.noteFiles.some(
            existing => existing.name === file.name && existing.size === file.size
          )
          if (!alreadyAdded) {
            this.noteFiles.push(file)
          }
        } else if (file) {
          invalidFound = true
        }
      })

      this.noteFileError = invalidFound ? 'Solo se permiten imágenes JPG, PNG, WEBP o GIF.' : ''
      event.target.value = ''
    },
    removeNoteFile(index) {
      this.noteFiles.splice(index, 1)
      if (!this.noteFiles.length) {
        this.noteFileError = ''
      }
    },
    submitNote() {
      if (this.noteSaving) {
        return
      }
      const texto = this.noteText.trim()
      if (!texto) {
        this.noteError = 'Escribe una nota antes de guardar.'
        return
      }

      this.noteSaving = true
      this.noteError = ''

      const propId = this.$route.params.id
      const formData = new FormData()
      formData.append('texto', texto)
      this.noteFiles.forEach((file) => {
        formData.append('imagenes', file)
      })

      axios.post(`${window.hostname}api/property_manager/propiedades/${propId}/notas`, formData)
        .then((res) => {
          if (res.data?.nota) {
            this.propertyNotes = [res.data.nota, ...this.propertyNotes]
          } else {
            this.fetchData()
          }
          this.showNoteModal = false
        })
        .catch((err) => {
          this.noteError = err?.response?.data?.message || 'No se pudo guardar la nota.'
        })
        .finally(() => {
          this.noteSaving = false
        })
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.property-detail-view {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.column-right {
  background-color: rgb(242, 247, 251);
  padding-top: 80px;
  padding-left: 260px;
  width: 100%;
  min-height: 133vh;
  min-height: 133dvh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.main-container {
  background-color: white;
  margin: 20px;
  padding: 20px 20px 10px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex: 1;
}

.page-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.back-btn :deep(svg) {
  width: 18px;
  height: 18px;
}

.sr-only {
  width: 0px;
}

.back-btn:hover {
  background: #f8fafc;
  color: #0f172a;
  border-color: #cbd5f5;
}

.header-content {
  flex: 1;
  min-width: 240px;
}

.breadcrumbs {
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;
}

.crumb-link {
  cursor: pointer;
}

.separator {
  margin: 0 8px;
  color: #cbd5e1;
}

.current {
  color: #2563eb;
  font-weight: 600;
}

.page-header h1 {
  font-size: 1.9rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
}

.lede {
  color: #64748b;
  margin: 0 0 16px;
}

.actions-bar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  padding: 10px 16px;
  font-weight: 700;
  border: none;
  cursor: pointer;
}

.btn-primary :deep(svg),
.btn-secondary :deep(svg) {
  width: 16px;
  height: 16px;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-secondary {
  background: white;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.btn-secondary:hover {
  background: #f8fafc;
}

.section-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
}

.images-card {
  max-width: 100%;
  overflow: hidden;
  min-width: 0;
}

.section-title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #94a3b8;
  margin-bottom: 20px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.2em;
}

.info-value-group {
  display: flex;
  gap: 12px;
  align-items: center;
}

.info-value-group .icon {
  color: #2563eb;
  font-size: 1.6rem;
}

.info-value-group .icon :deep(svg) {
  width: 1.6rem;
  height: 1.6rem;
}

.main-text {
  font-weight: 700;
  color: #0f172a;
}

.sub-text {
  color: #64748b;
  font-size: 0.9rem;
}

.card-header-clean {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.section-title-clean {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.count-badge-clean {
  background: #eef2ff;
  color: #4338ca;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.status-badge {
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.status-badge.blue {
  background: #dbeafe;
  color: #1d4ed8;
}

.requests-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.request-item {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 16px;
  align-items: center;
  background: #f8fafc;
  cursor: pointer;
  text-align: left;
}

.request-item:hover {
  border-color: #cbd5f5;
  background: #f1f5ff;
}

.request-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.request-icon {
  display: flex;
  align-items: center;
}

.service-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  background: #e0f2fe;
}

.service-icon :deep(svg) {
  width: 24px;
  height: 24px;
}

.service-icon__svg :deep(svg) {
  width: 24px;
  height: 24px;
  display: block;
}

.request-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #0f172a;
}

.request-meta {
  margin: 0;
  color: #64748b;
  font-size: 0.85rem;
}

.request-assigned {
  margin: 0;
  color: #475569;
  font-size: 0.85rem;
}

.request-assigned span {
  font-weight: 600;
}

.req-avatars {
  display: flex;
  align-items: center;
  gap: 6px;
}

.req-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #e2e8f0;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #475569;
}

.req-avatar--more {
  background: #f1f5f9;
  color: #475569;
  font-size: 0.7rem;
  padding: 0 6px;
  width: auto;
  border-radius: 999px;
}

.req-avatar-initials {
  line-height: 1;
}

.status-pill {
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.status-pill--active {
  background: #dcfce7;
  color: #15803d;
}

.status-pill--pending {
  background: #fef9c3;
  color: #92400e;
}

.status-pill--scheduled {
  background: #fef9c3;
  color: #92400e;
}

.status-pill--confirmed {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-pill--progress {
  background: #dcfce7;
  color: #15803d;
}

.status-pill--neutral {
  background: #e2e8f0;
  color: #475569;
}

.history-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.history-list {
  display: grid;
  gap: 16px;
}

.history-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #f8fafc;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.history-date {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 600;
}

.history-badge {
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.history-badge--service {
  background: #e0f2fe;
  color: #0284c7;
}

.history-badge--note {
  background: #fef3c7;
  color: #b45309;
}

.btn-secondary--compact {
  padding: 6px 12px;
  font-size: 0.85rem;
}

.history-title {
  margin: 0 0 6px;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.history-meta {
  margin: 0 0 4px;
  font-size: 0.85rem;
  color: #475569;
}

.text-btn {
  border: none;
  background: none;
  color: #2563eb;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
  max-width: 100%;
  min-width: 0;
}

.image-wrapper {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.image-wrapper.is-main {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.property-image-btn {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: block;
  width: 100%;
}

.property-image-btn img {
  width: 100%;
  height: 140px;
  object-fit: cover;
}

.image-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
}

.main-photo-btn,
.delete-photo-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
}

.main-photo-btn :deep(svg),
.delete-photo-btn :deep(svg) {
  width: 16px;
  height: 16px;
}

.delete-photo-btn {
  color: #dc2626;
}

.add-photo-btn {
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  padding: 20px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 600;
  color: #64748b;
}

.circle-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #dbeafe;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle-icon :deep(svg) {
  width: 24px;
  height: 24px;
}

.image-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 24px;
}

.image-modal-card {
  position: relative;
  background: #0f172a;
  border-radius: 20px;
  max-width: 1100px;
  width: 100%;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.35);
  overflow: hidden;
}

.image-modal-photo {
  display: block;
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
  background: #0f172a;
}

.image-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: #0f172a;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
}

.image-modal-close :deep(svg) {
  width: 18px;
  height: 18px;
}

.image-modal-close:hover {
  background: #ffffff;
  transform: scale(1.05);
}

.image-modal-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  background: rgba(15, 23, 42, 0.75);
  color: #e2e8f0;
  font-size: 0.85rem;
  font-weight: 600;
}

.image-modal-footer :deep(svg) {
  width: 16px;
  height: 16px;
}

.notes-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.note-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #f8fafc;
}

.note-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 8px;
}

.note-meta :deep(svg) {
  width: 16px;
  height: 16px;
}

.note-text {
  margin: 0 0 12px;
  color: #0f172a;
}

.note-images {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.note-image-btn {
  border: none;
  background: none;
  padding: 0;
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
}

.note-image-btn img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty-list,
.empty-images,
.error-state {
  text-align: center;
  color: #64748b;
  padding: 24px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #64748b;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  animation: spin 1s linear infinite;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  isolation: isolate;
}

.modal-backdrop::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  z-index: 0;
}

.modal-container {
  background-color: #ffffff !important;
  position: relative;
  border-radius: 20px;
  width: 100%;
  max-width: 650px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
  opacity: 1 !important;
  isolation: isolate;
  z-index: 1;
}

.modal-container--compact {
  max-width: 560px;
}

.modal-header {
  padding: 24px 28px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
}

.close-modal-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-modal-btn :deep(svg) {
  width: 18px;
  height: 18px;
}

.modal-body {
  padding: 28px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 20px 28px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.form-grid {
  display: grid;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field__label {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.95rem;
}

input, select, textarea {
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  font-size: 1rem;
  outline: none;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
}

input:focus, select:focus, textarea:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-divider {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #94a3b8;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 8px 0;
}

.form-divider::before, .form-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.field__error {
  background: #fef2f2;
  color: #b91c1c;
  padding: 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin: 0;
}

.field__error :deep(svg) {
  width: 16px;
  height: 16px;
}

.file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-chip {
  background: #e2e8f0;
  border-radius: 999px;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
}

.file-chip button {
  border: none;
  background: none;
  cursor: pointer;
}

@media (max-width: 768px) {
  .column-right {
    width: 100%;
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 70px;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
